/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/**
 * Message Templates Service - All hardcoded text messages and message objects
 * Centralized location for all user-facing text content and message structures
 */

import type { AIResponseMessage, StepFormMessage } from '../data-layer/types';

import {
  MESSAGE_IDS,
  MESSAGE_SUBTYPES,
  MESSAGE_TYPES,
  STEP_NAMES,
} from './constantsService';

// Welcome messages
export const WELCOME_MESSAGES = {
  MAIN: 'Lets Get Started!',
} as const;

// Edit Flow Welcome Message
export const EDIT_WELCOME_MESSAGE = {
  WELCOME:
    'Hey, How can I help you with configuration? on selection of particular configuration you can review or update the configuration',
} as const;

// Button labels
export const BUTTON_LABELS = {
  CONTINUE: 'Continue',
  UPDATE: 'Update',
  YES: 'Yes',
  NO: 'No',
} as const;

// Message Templates - Objects without timestamp
export const MESSAGE_TEMPLATES = {
  WELCOME: {
    id: MESSAGE_IDS.WELCOME,
    type: MESSAGE_TYPES.AI_RESPONSE,
    subType: MESSAGE_SUBTYPES.START,
    text: WELCOME_MESSAGES.MAIN,
  } as Omit<AIResponseMessage, 'timestamp'>,

  FORM_GENERAL: {
    id: MESSAGE_IDS.FORM_GENERAL,
    type: MESSAGE_TYPES.STEP_FORM,
    subType: MESSAGE_SUBTYPES.BASIC_CONFIG,
    isCollapseable: true,
    buttonLabel: BUTTON_LABELS.CONTINUE,
    stepName: STEP_NAMES.GENERAL,
  } as Omit<StepFormMessage, 'timestamp' | 'heading' | 'fields' | 'formData'>,

  FORM_CONNECTION: {
    id: MESSAGE_IDS.FORM_CONNECTION,
    type: MESSAGE_TYPES.STEP_FORM,
    subType: MESSAGE_SUBTYPES.CONNECTION_CONFIG,
    isCollapseable: true,
    buttonLabel: BUTTON_LABELS.CONTINUE,
    stepName: STEP_NAMES.CONNECTION,
  } as Omit<StepFormMessage, 'timestamp' | 'heading' | 'fields' | 'formData'>,

  FORM_CREATE_ACCOUNT: {
    id: MESSAGE_IDS.FORM_CREATE_ACCOUNT,
    type: MESSAGE_TYPES.STEP_FORM,
    subType: MESSAGE_SUBTYPES.CREATE_ACCOUNT_FORM,
    isCollapseable: true,
    buttonLabel: BUTTON_LABELS.CONTINUE,
    stepName: STEP_NAMES.CREATE_ACCOUNT,
  } as Omit<StepFormMessage, 'timestamp' | 'heading' | 'fields' | 'formData'>,

  FORM_UPDATE_ACCOUNT: {
    id: 'form-update-account',
    type: MESSAGE_TYPES.STEP_FORM,
    subType: MESSAGE_SUBTYPES.UPDATE_ACCOUNT_FORM,
    isCollapseable: true,
    buttonLabel: BUTTON_LABELS.CONTINUE,
    stepName: STEP_NAMES.UPDATE_ACCOUNT,
  } as Omit<StepFormMessage, 'timestamp' | 'heading' | 'fields' | 'formData'>,

  FORM_ENABLE_ACCOUNT: {
    id: 'form-enable-account',
    type: MESSAGE_TYPES.STEP_FORM,
    subType: MESSAGE_SUBTYPES.ENABLE_ACCOUNT_FORM,
    isCollapseable: true,
    buttonLabel: BUTTON_LABELS.CONTINUE,
    stepName: STEP_NAMES.ENABLE_ACCOUNT,
  } as Omit<StepFormMessage, 'timestamp' | 'heading' | 'fields' | 'formData'>,

  FORM_DISABLE_ACCOUNT: {
    id: 'form-disable-account',
    type: MESSAGE_TYPES.STEP_FORM,
    subType: MESSAGE_SUBTYPES.DISABLE_ACCOUNT_FORM,
    isCollapseable: true,
    buttonLabel: BUTTON_LABELS.CONTINUE,
    stepName: STEP_NAMES.DISABLE_ACCOUNT,
  } as Omit<StepFormMessage, 'timestamp' | 'heading' | 'fields' | 'formData'>,

  FORM_REMOVE_ACCOUNT: {
    id: 'form-remove-account',
    type: MESSAGE_TYPES.STEP_FORM,
    subType: MESSAGE_SUBTYPES.REMOVE_ACCOUNT_FORM,
    isCollapseable: true,
    buttonLabel: BUTTON_LABELS.CONTINUE,
    stepName: STEP_NAMES.REMOVE_ACCOUNT,
  } as Omit<StepFormMessage, 'timestamp' | 'heading' | 'fields' | 'formData'>,

  FORM_ADD_ACCESS: {
    id: 'form-add-access',
    type: MESSAGE_TYPES.STEP_FORM,
    subType: MESSAGE_SUBTYPES.ADD_ACCESS_FORM,
    isCollapseable: true,
    buttonLabel: BUTTON_LABELS.CONTINUE,
    stepName: STEP_NAMES.ADD_ACCESS,
  } as Omit<StepFormMessage, 'timestamp' | 'heading' | 'fields' | 'formData'>,

  FORM_REMOVE_ACCESS: {
    id: 'form-remove-access',
    type: MESSAGE_TYPES.STEP_FORM,
    subType: (() => MESSAGE_SUBTYPES.REMOVE_ACCESS_FORM)(),
    isCollapseable: true,
    buttonLabel: BUTTON_LABELS.CONTINUE,
    stepName: STEP_NAMES.REMOVE_ACCESS,
  } as Omit<StepFormMessage, 'timestamp' | 'heading' | 'fields' | 'formData'>,
} as const;

/**
 * Create message with current timestamp
 */
export const createMessage = <T extends Record<string, unknown>>(
  template: T
): T & { timestamp: number } => ({
  ...template,
  timestamp: Date.now(),
});

/**
 * Form message factory function
 */
export const createFormMessage = (
  stepName: keyof typeof STEP_NAMES,
  options: {
    heading?: string;
    fields?: unknown[];
    formData?: Record<string, unknown>;
    buttonLabel?: string;
    isEditMode?: boolean;
    completedSteps?: string[];
  } = {}
): StepFormMessage => {
  // Map step names to their templates
  const templateMap = {
    GENERAL: MESSAGE_TEMPLATES.FORM_GENERAL,
    CONNECTION: MESSAGE_TEMPLATES.FORM_CONNECTION,
    CREATE_ACCOUNT: MESSAGE_TEMPLATES.FORM_CREATE_ACCOUNT,
    ADD_ACCESS: MESSAGE_TEMPLATES.FORM_ADD_ACCESS,
    REMOVE_ACCESS: MESSAGE_TEMPLATES.FORM_REMOVE_ACCESS,
    UPDATE_ACCOUNT: MESSAGE_TEMPLATES.FORM_UPDATE_ACCOUNT,
    ENABLE_ACCOUNT: MESSAGE_TEMPLATES.FORM_ENABLE_ACCOUNT,
    DISABLE_ACCOUNT: MESSAGE_TEMPLATES.FORM_DISABLE_ACCOUNT,
    REMOVE_ACCOUNT: MESSAGE_TEMPLATES.FORM_REMOVE_ACCOUNT,
  };

  const template = templateMap[stepName];

  let { buttonLabel } = options;

  if (!buttonLabel) {
    buttonLabel = template.buttonLabel;
  }

  return {
    ...createMessage(template),
    heading: options.heading ?? `${stepName} Details`,
    fields: options.fields ?? [],
    formData: options.formData ?? {},
    stepName: STEP_NAMES[stepName],
    buttonLabel,
  } as StepFormMessage;
};
